<template>
  <div class="flex flex-col max-w-lg">
    <div class="mb-4">
      <h2 class="text-base font-semibold text-gray-800">Active SMS gateway</h2>
      <p class="text-sm text-gray-500 mt-1">
        Choose which provider sends outbound SMS (UGPASS, Instacom or Infobip).
      </p>
    </div>

    <div class="relative min-h-[120px]">
      <div
        v-if="loading"
        class="absolute inset-0 z-10 flex flex-col items-center justify-center rounded border border-gray-200 bg-white/90"
      >
        <i class="fa-solid fa-circle-notch fa-spin text-2xl text-primary"></i>
        <p class="mt-2 text-sm text-gray-500">Loading SMS gateway...</p>
      </div>

      <form
        class="bg-white border border-gray-200 rounded-lg p-5 space-y-4"
        :class="{ invisible: loading }"
        @submit.prevent="saveGateway"
      >
        <fieldset class="space-y-3">
          <legend class="text-xs font-semibold text-gray-600 uppercase tracking-wide mb-1">
            Provider
          </legend>
          <label
            v-for="option in GATEWAY_OPTIONS"
            :key="option.value"
            class="flex items-center gap-3 cursor-pointer rounded-md border px-3 py-2.5 transition"
            :class="
              gateway === option.value
                ? 'border-primary bg-primary/5'
                : 'border-gray-200 hover:border-gray-300'
            "
          >
            <input
              v-model="gateway"
              type="radio"
              class="w-4 h-4 text-primary border-gray-300 focus:ring-primary"
              :value="option.value"
              :disabled="saving || loading"
            />
            <span class="text-sm font-medium text-gray-800">{{ option.label }}</span>
          </label>
        </fieldset>

        <div class="pt-1 flex justify-end">
          <button
            type="submit"
            class="btn-primary px-3 py-2 rounded-sm text-white"
            :disabled="saving || loading"
          >
            Save
            <i v-if="saving" class="fa fa-spinner fa-spin ml-1"></i>
          </button>
        </div>
      </form>
    </div>
  </div>
</template>

<script setup lang="ts">
import { onMounted, ref } from "vue";
import { useToast } from "@/composables";
import { extractErrorMessage } from "./gatewayApi";
import {
  SMS_GATEWAY_OPTIONS as GATEWAY_OPTIONS,
  fetchSmsGateway,
  saveSmsGateway,
  type SmsGatewayValue,
} from "./smsGatewayApi";

const { errorToast, successToast } = useToast();

const loading = ref(false);
const saving = ref(false);
const gateway = ref<SmsGatewayValue>("UGPASS");

const loadGateway = async () => {
  loading.value = true;
  try {
    const parsed = await fetchSmsGateway();
    if (parsed) gateway.value = parsed;
  } catch (error: unknown) {
    errorToast("Error", extractErrorMessage(error, "Could not load SMS gateway configuration"));
  } finally {
    loading.value = false;
  }
};

const saveGateway = async () => {
  saving.value = true;
  try {
    await saveSmsGateway(gateway.value);
    successToast("Success", "SMS gateway updated");
    await loadGateway();
  } catch (error: unknown) {
    errorToast("Error", extractErrorMessage(error, "Could not update SMS gateway"));
  } finally {
    saving.value = false;
  }
};

onMounted(() => loadGateway());

defineExpose({ refresh: loadGateway });
</script>
